import { Avatar, Box, Button, CircularProgress, MenuItem, Select, styled, TextareaAutosize, TextField, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import useStore, { useStoreActions } from "../../../store";
import athenaService from "../../../services/athenaService";
import { format } from "date-fns";

const StyledTextArea = styled(TextareaAutosize)(({ theme }) => ({
  width: "100%",
  minHeight: 50, // Reduced to 1/3 of original height (150px)
  padding: 12,
  borderRadius: 4,
  border: "1px solid #e0e0e0",
  fontFamily: theme.typography.fontFamily,
  fontSize: 16,
  backgroundColor: "transparent",
  "&:focus": {
    outline: "none",
    borderColor: "#333",
  },
}));

function AppointmentInsurance({ setScreen }) {

  const { patient, appointmentDetails, selectedDepartment, selectedProvider, provider } = useStore()
  const { setInsuranceDetails } = useStoreActions()
  const [error, setError ] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const [providerImage, setProviderImage] = useState(null)
  
  // Required fields for patient insurance API
  const [insurancePackageId, setInsurancePackageId] = useState("");
  const [insuranceIdNumber, setInsuranceIdNumber] = useState("");
  const [sequenceNumber, setSequenceNumber] = useState(1); // Default to primary insurance
  const [policyNumber, setPolicyNumber] = useState("");
  const [policyHolderSex, setPolicyHolderSex] = useState("");
  const [policyHolderFirstName, setPolicyHolderFirstName] = useState("");
  const [policyHolderLastName, setPolicyHolderLastName] = useState("");
  const [policyHolderDob, setPolicyHolderDob] = useState(""); // New field for policy holder DOB
  const [relationshipId, setRelationshipId] = useState(1); // Default to self
  
  // Insurance package options based on real data from API
  const [insurancePackages, setInsurancePackages] = useState([
    { id: "0", name: "Self Pay (No Insurance)" },
    { id: 98546, name: "Medicare" },
    { id: 15310, name: "Blue Cross" },
    { id: 74, name: "Cigna" },
    { id: 2150, name: "Blue Shield" },
    { id: 23344, name: "Aetna" },
    { id: 660279, name: "Health Net" },
    { id: 4953, name: "United Healthcare" },
    { id: 655450, name: "Tricare" },
    { id: 581754, name: "Adventist Health" },
    { id: 19961, name: "USC Health Plan" },
    { id: 190449, name: "Optum Medical Group" },
  ]);
  
  // Relationship options with complete list of athena IDs
  const relationships = [
    { id: 1, name: "Self" },
    { id: 2, name: "Spouse" },
    { id: 3, name: "Child" },
    { id: 4, name: "Other" },
    { id: 5, name: "Grandparent" },
    { id: 6, name: "Grandchild" },
    { id: 7, name: "Uncle or Aunt" },
    { id: 9, name: "Foster Child" },
    { id: 10, name: "Ward" },
    { id: 11, name: "Stepson or Stepdaughter" },
    { id: 12, name: "Employee" },
    { id: 13, name: "Unknown" },
    { id: 14, name: "Handicapped Dependent" },
    { id: 15, name: "Sponsored Dependent" },
    { id: 16, name: "Dependent of a Minor Dependent" },
    { id: 17, name: "Significant Other" },
    { id: 18, name: "Mother" },
    { id: 19, name: "Father" },
    { id: 21, name: "Emancipated Minor" },
    { id: 22, name: "Organ Donor" },
    { id: 23, name: "Cadaver Donor" },
    { id: 24, name: "Injured Plaintiff" },
    { id: 25, name: "Child (Ins. not Financially Respons.)" },
    { id: 26, name: "Life Partner" },
    { id: 27, name: "Child (Mother's Insurance)" },
    { id: 28, name: "Child (Father's Insurance)" },
    { id: 29, name: "Child (of Mother, Ins. not Financially Respons.)" },
    { id: 30, name: "Child (of Father, Ins. not Financially Respons.)" },
    { id: 31, name: "Stepson or Stepdaughter (Stepmother's Insurance)" },
    { id: 32, name: "Stepson or Stepdaughter (Stepfather's Insurance)" }
  ];
  
  // Notes and referral
  const [notes, setNotes] = useState("");
  const [referralSource, setReferralSource] = useState("");
  
  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };

  // Format date input as MM/DD/YYYY as user types
  const formatDateInput = (value) => {
    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format with slashes
    let formattedValue = '';
    if (digits.length > 0) {
      // Add first part (MM)
      formattedValue = digits.substring(0, Math.min(2, digits.length));
      
      // Add second part (DD) with slash
      if (digits.length > 2) {
        formattedValue += '/' + digits.substring(2, Math.min(4, digits.length));
      }
      
      // Add third part (YYYY) with slash
      if (digits.length > 4) {
        formattedValue += '/' + digits.substring(4, Math.min(8, digits.length));
      }
    }
    
    return formattedValue;
  };

  useEffect(() => {
    // Try to load the provider image if the complete provider object is available
    if (provider) {
      // Get provider name properly based on available fields
      const providerName = `${provider.firstname || ''} ${provider.lastname || ''}`;
      
      // Log provider object for debugging
      console.log("Complete provider object in AppointmentInsurance:", JSON.stringify(provider));
      
      // Safety check - don't proceed if name is empty
      if (!providerName.trim()) {
        console.warn("Provider name is empty, cannot load image");
        return;
      }
      
      console.log("Loading image for provider in AppointmentInsurance:", providerName);
      
      // Convert provider name to lowercase and replace spaces with dashes for filename
      const providerFileName = providerName.toLowerCase().replace(/\s+/g, '-');
      import(`../../../assets/images/providers/${providerFileName}.png`)
        .then(image => {
          console.log("Successfully loaded provider image for", providerName);
          setProviderImage(image.default);
        })
        .catch(err => {
          console.error("Could not load provider image:", err);
          setProviderImage(null);
        });
    } else {
      console.log("No complete provider object available in AppointmentInsurance", 
        provider, "Using selectedProvider ID:", selectedProvider);
    }
  }, [provider]); // Now depends on the complete provider object
  
  // Auto-fill from patient data when "Self" is selected
  useEffect(() => {
    // Get the patient details from the appointment details that were saved earlier
    if (relationshipId === 1 && appointmentDetails) {
      console.log("Auto-filling policy holder info from appointmentDetails:", appointmentDetails);
      
      // Populate policy holder fields with patient's information
      if (appointmentDetails.firstName) {
        setPolicyHolderFirstName(appointmentDetails.firstName);
      }
      
      if (appointmentDetails.lastName) {
        setPolicyHolderLastName(appointmentDetails.lastName);
      }
      
      if (appointmentDetails.sex) {
        setPolicyHolderSex(appointmentDetails.sex === 'male' ? 'Male' : 'Female');
      }
      
      if (appointmentDetails.dateOfBirth) {
        setPolicyHolderDob(appointmentDetails.dateOfBirth);
      }
    }
  }, [relationshipId, appointmentDetails]); // Trigger when relationship changes

  const handleSubmit = async () => {
    // Save insurance details to the global store
    setInsuranceDetails({ 
      insurancePackageId,
      insuranceIdNumber,
      sequenceNumber,
      policyNumber,
      policyHolderSex,
      policyHolderFirstName,
      policyHolderLastName,
      policyHolderDob, // Include policy holder DOB in the saved details
      relationshipId,
      notes,
      referralSource
    });

    setLoading(true);
    try {
      // Handle insurance creation based on insurancePackageId
      if (insurancePackageId) {
        try {
          // Convert insurancePackageId to a number for the API
          const insuranceIdAsNumber = parseInt(insurancePackageId, 10);
          
          // Check if this is self-pay (insurancePackageId === '0')
          if (insurancePackageId === '0') {
            // For self-pay, send only the insurancepackageid parameter
            console.log('Creating self-pay insurance with package ID:', insuranceIdAsNumber);
            
            const response = await athenaService.createPatientInsurance(
              patient, // First parameter: patientId
              { // Second parameter: insurance data object with only insurancepackageid
                insurancepackageid: insuranceIdAsNumber
              }
            );
            
            console.log('Self-pay insurance created successfully');
          } else {
            // For regular insurance, validate required fields
            if (!insuranceIdNumber) {
              setError("Please enter an insurance ID number");
              setLoading(false);
              return;
            }
            
            if (!policyNumber) {
              setError("Please enter a policy/group number");
              setLoading(false);
              return;
            }
            
            if (!relationshipId) {
              setError("Please select the relationship to the policy holder");
              setLoading(false);
              return;
            }
            
            if (!policyHolderFirstName) {
              setError("Please enter the policy holder's first name");
              setLoading(false);
              return;
            }
            
            if (!policyHolderLastName) {
              setError("Please enter the policy holder's last name");
              setLoading(false);
              return;
            }
            
            // Validate policy holder DOB format and validity
            if (!policyHolderDob || !policyHolderDob.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              setError("Please enter a valid date of birth in MM/DD/YYYY format");
              setLoading(false);
              return;
            }
            
            // Check if DOB is a valid date
            try {
              const [month, day, year] = policyHolderDob.split('/').map(num => parseInt(num, 10));
              const date = new Date(year, month - 1, day);
              if (isNaN(date.getTime()) || 
                  date.getMonth() !== month - 1 || 
                  date.getDate() !== day || 
                  date.getFullYear() !== year ||
                  year < 1900 ||
                  year > new Date().getFullYear()) {
                setError("Please enter a valid date of birth");
                setLoading(false);
                return;
              }
            } catch (e) {
              setError("Please enter a valid date of birth");
              setLoading(false);
              return;
            }
            
            // Format data properly before submission
            const formattedSex = policyHolderSex === "Male" ? "M" : policyHolderSex === "Female" ? "F" : policyHolderSex;
            
            // Log for debugging
            console.log('Creating insurance with package ID:', insuranceIdAsNumber);
            console.log('Policy holder sex (formatted):', formattedSex);
            
            // Send complete insurance data
            const response = await athenaService.createPatientInsurance(
              patient, // First parameter: patientId
              { // Second parameter: insurance data object
                insurancepackageid: insuranceIdAsNumber,
                insuranceidnumber: insuranceIdNumber,
                insurancepolicyholder: relationshipId === 1 ? "SELF" : "OTHER",
                sequencenumber: sequenceNumber,
                policynumber: policyNumber,
                insurancepolicyholdersex: formattedSex, // Use the properly formatted sex value
                insurancepolicyholderfirstname: policyHolderFirstName, 
                insurancepolicyholderlastname: policyHolderLastName,
                relationshiptoinsuredid: relationshipId,
                insurancepolicyholderdob: policyHolderDob // Make sure this is formatted as MM/DD/YYYY
              }
            );
            
            console.log('Insurance created successfully');
          }
        } catch (error) {
          console.error('Error creating insurance:', error);
          
          // Check for the specific 409 error about existing insurance
          console.log('Error object structure:', error);
          
          if (
              error.status === 409 || 
              (error.response && error.response.status === 409) || 
              (error.statusText === 'Conflict') ||
              (error.data && error.data.success === false && error.data.message && error.data.message.includes('409')) ||
              (error.message && error.message.includes('status code 409'))
          ) {
            console.log('Patient already has insurance. Skipping insurance creation and proceeding with appointment booking.');
            // Continue with the appointment booking flow, no need to set error
          } else {
            // For any other error, show the error message
            setError(error.message || "Failed to create insurance. Please try again.");
            return; // Stop execution on non-409 errors
          }
          setLoading(false);
          return;
        }
      } else {
        console.log('No insurance package ID provided - skipping insurance creation');
      }
      
      // Book the appointment
      try {
        const response = await athenaService.bookAppointment({ 
          appointmentid: appointmentDetails.id,
          patientid: patient,
          appointmenttypeid: appointmentDetails.appointmenttypeid,
          notes: notes // Add booking notes
        });
        
        console.log('Appointment booked successfully:', response);
        setScreen(4); // Move to confirmation screen
      } catch (error) {
        console.error('Error booking appointment:', error);
        setError(error.message || "Failed to book appointment. Please try again.");
      }
    } catch (error) {
      console.error('Error in insurance/appointment process:', error);
      setError(error.message || "Failed to process your request");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div style={{ maxWidth: 1000, margin: "0 auto", width: '100%' }}>
        {/* Provider and Appointment Details */}
        <Box sx={{ p: { xs: 2, sm: 4 }, mb: 2 }}>
          {/* Provider Info Header - Matching AppointmentUserForm styling */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <Avatar 
              src={providerImage || "/profile.jpg"} 
              alt={provider ? (provider.displayname || `${provider.firstname || ''} ${provider.lastname || ''}`) : 'Provider'} 
              sx={{
                width: 80,
                height: 80,
                border: 2,
                borderColor: "#383352",
              }} 
            />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {appointmentDetails && appointmentDetails.date ? 
                  `${format(new Date(appointmentDetails.date), 'EEEE, MMM d - h:mm a')}` : 
                  'Appointment Date and Time'}
              </Typography>
              <Typography variant="button">
                {provider ? 
                  (provider.displayname || 
                   (provider.firstname || provider.lastname ? 
                    `Dr ${provider.firstname || ''} ${provider.lastname || ''}` : 
                    'Provider')) : 
                  'Provider'}
              </Typography>
            </Box>
          </Box>

          {/* Insurance Form Fields - All with consistent width and spacing */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>Insurance Provider *</Typography>
            <Select
              fullWidth
              size="small"
              value={insurancePackageId}
              onChange={(e) => {
                setInsurancePackageId(e.target.value);
                // Clear fields if self-pay is selected
                if (e.target.value === '0') {
                  setInsuranceIdNumber('');
                  setPolicyNumber('');
                  setPolicyHolderFirstName('');
                  setPolicyHolderLastName('');
                  setPolicyHolderSex('');
                }
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return "Select Insurance Provider";
                if (selected === '0') return "Self Pay (No Insurance)";
                const selectedPackage = insurancePackages.find(p => p.id === parseInt(selected));
                return selectedPackage ? selectedPackage.name : selected;
              }}
              sx={{
                bgcolor: "transparent",
                borderRadius: 1,
                height: '40px',
                '& .MuiSelect-select': {
                  padding: '10px 14px',
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#333",
                }
              }}
              required
            >
              <MenuItem value="" disabled>Select Insurance Provider</MenuItem>
              {insurancePackages.map(pkg => (
                <MenuItem key={pkg.id} value={pkg.id}>{pkg.name}</MenuItem>
              ))}
              {/* Removed duplicate Self Pay entry since it's already in insurancePackages array */}
            </Select>
          </Box>

          {/* Only show insurance fields if an insurance is selected and not self-pay */}
          {insurancePackageId && insurancePackageId !== '0' && (
            <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>Insurance ID Number *</Typography>
              <TextField
                value={insuranceIdNumber}
                onChange={(e) => setInsuranceIdNumber(e.target.value)}
                placeholder="e.g., WT38920333"
                size="small"
                fullWidth
                required
                variant="outlined"
                sx={{
                    bgcolor: "transparent",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#333",
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>Group/Policy Number *</Typography>
                <TextField
                  value={policyNumber}
                  onChange={(e) => setPolicyNumber(e.target.value)}
                  placeholder="Group/Policy Number"
                  size="small"
                  fullWidth
                  required
                  variant="outlined"
                  sx={{
                    bgcolor: "transparent",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#333",
                      },
                    },
                  }}
                />
              </Box>

              {/* Insurance Type field - hidden visually but still functional */}
              <Box sx={{ mb: 3, display: 'none' }}>
                <Typography variant="body1" gutterBottom>Insurance Type *</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={sequenceNumber}
                  onChange={(e) => setSequenceNumber(e.target.value)}
                  sx={{
                    bgcolor: "transparent",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                  }}
                  required
                >
                  <MenuItem value={1}>Primary Insurance</MenuItem>
                  <MenuItem value={2}>Secondary Insurance</MenuItem>
                </Select>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>Relationship to Policy Holder *</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={relationshipId}
                  onChange={(e) => {
                    setRelationshipId(e.target.value);
                    
                    // Clear fields when changing relationship (except when selecting Self)
                    if (e.target.value !== 1) {
                      setPolicyHolderFirstName('');
                      setPolicyHolderLastName('');
                      setPolicyHolderSex('');
                      setPolicyHolderDob('');
                    } else if (e.target.value === 1 && appointmentDetails) {
                      // Auto-fill for Self relationship
                      setPolicyHolderFirstName(appointmentDetails.firstName || '');
                      setPolicyHolderLastName(appointmentDetails.lastName || '');
                      setPolicyHolderSex(appointmentDetails.sex === 'male' ? 'M' : appointmentDetails.sex === 'female' ? 'F' : '');
                      setPolicyHolderDob(appointmentDetails.dateOfBirth || '');
                    }
                  }}
                  sx={{
                    bgcolor: "transparent",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                  }}
                  required
                >
                  {/* Group relationships to make the long list more manageable */}
                  <MenuItem value="" disabled>Select Relationship</MenuItem>
                  <MenuItem value={1}>Self</MenuItem>
                  <MenuItem value={2}>Spouse</MenuItem>
                  <MenuItem value={17}>Significant Other</MenuItem>
                  <MenuItem value={26}>Life Partner</MenuItem>
                  <MenuItem disabled>──────── Family Relations ────────</MenuItem>
                  <MenuItem value={3}>Child</MenuItem>
                  <MenuItem value={18}>Mother</MenuItem>
                  <MenuItem value={19}>Father</MenuItem>
                  <MenuItem value={5}>Grandparent</MenuItem>
                  <MenuItem value={6}>Grandchild</MenuItem>
                  <MenuItem value={7}>Uncle or Aunt</MenuItem>
                  <MenuItem value={9}>Foster Child</MenuItem>
                  <MenuItem value={10}>Ward</MenuItem>
                  <MenuItem value={11}>Stepson or Stepdaughter</MenuItem>
                  <MenuItem disabled>──────── Other Relations ────────</MenuItem>
                  <MenuItem value={4}>Other</MenuItem>
                  <MenuItem value={12}>Employee</MenuItem>
                  <MenuItem value={13}>Unknown</MenuItem>
                  <MenuItem value={14}>Handicapped Dependent</MenuItem>
                  <MenuItem value={24}>Injured Plaintiff</MenuItem>
                  {/* Additional relationships that are less commonly used */}
                  <MenuItem value={15}>Sponsored Dependent</MenuItem>
                  <MenuItem value={16}>Dependent of a Minor Dependent</MenuItem>
                  <MenuItem value={21}>Emancipated Minor</MenuItem>
                  <MenuItem value={22}>Organ Donor</MenuItem>
                  <MenuItem value={23}>Cadaver Donor</MenuItem>
                  <MenuItem value={25}>Child (Ins. not Financially Respons.)</MenuItem>
                  <MenuItem value={27}>Child (Mother's Insurance)</MenuItem>
                  <MenuItem value={28}>Child (Father's Insurance)</MenuItem>
                  <MenuItem value={29}>Child (of Mother, Ins. not Financially Respons.)</MenuItem>
                  <MenuItem value={30}>Child (of Father, Ins. not Financially Respons.)</MenuItem>
                  <MenuItem value={31}>Stepson or Stepdaughter (Stepmother's Insurance)</MenuItem>
                  <MenuItem value={32}>Stepson or Stepdaughter (Stepfather's Insurance)</MenuItem>
                </Select>
              </Box>

              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Policy Holder Information</Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>Policy Holder First Name *</Typography>
                <TextField
                  value={policyHolderFirstName}
                  onChange={(e) => setPolicyHolderFirstName(e.target.value)}
                  placeholder="First Name"
                  size="small"
                  fullWidth
                  required
                  variant="outlined"
                  sx={{
                    bgcolor: "transparent",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#333",
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>Policy Holder Last Name *</Typography>
                <TextField
                  value={policyHolderLastName}
                  onChange={(e) => setPolicyHolderLastName(e.target.value)}
                  placeholder="Last Name"
                  size="small"
                  fullWidth
                  required
                  variant="outlined"
                  sx={{
                    bgcolor: "transparent",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#333",
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" gutterBottom>Policy Holder Sex</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={policyHolderSex}
                  onChange={(e) => setPolicyHolderSex(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return "Select";
                    if (selected === "M") return "Male";
                    if (selected === "F") return "Female";
                    return selected; // Fallback
                  }}
                  sx={{
                    bgcolor: "transparent",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                  }}
                >
                  <MenuItem value="" disabled>Select</MenuItem>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                </Select> 
              </Box>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" gutterBottom>Policy Holder Date of Birth *</Typography>
                <TextField
                  value={policyHolderDob}
                  onChange={(e) => setPolicyHolderDob(formatDateInput(e.target.value))}
                  placeholder="MM/DD/YYYY"
                  size="small"
                  fullWidth
                  required
                  variant="outlined"
                  sx={{
                    bgcolor: "transparent",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#333",
                      },
                    },
                  }}
                  inputProps={{
                    maxLength: 10,
                    placeholder: "MM/DD/YYYY"
                  }}
                  helperText="Format: MM/DD/YYYY"
                />
              </Box>
            </>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="body1">Additional notes for the practice</Typography>
            <StyledTextArea 
              minRows={3} 
              maxRows={6} 
              placeholder="Any additional information the practice should know" 
              value={notes} 
              onChange={handleNotesChange} 
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
              <Typography variant="body2" color="textSecondary">
                {notes.length}/250
              </Typography>
            </Box>
          </Box>

          {/* Referral Source */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1">How did you hear about us?</Typography>
            <Select
              fullWidth
              size="small"
              value={referralSource}
              onChange={(e) => setReferralSource(e.target.value)}
              displayEmpty
              renderValue={(selected) => (selected ? selected : "Select")}
              sx={{
                bgcolor: "transparent",
                borderRadius: 1,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="Google">Google</MenuItem>
              <MenuItem value="Friend">Friend or Family</MenuItem>
              <MenuItem value="Social Media">Social Media</MenuItem>
              <MenuItem value="Insurance">Insurance Provider</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </Box>
        </Box>
      </div>

      {error && (
        <Box mt={3} p={2} bgcolor="#ffebee" borderRadius={1}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Box sx={{mt: 4, display: "flex", justifyContent: "space-between" }}>
        <Button
          disabled={isLoading}
          onClick={()=>setScreen(2)}
          variant="outlined"
          sx={{
            px: 4,
            color: "#383352",
            borderColor: "#383352",
          }}
          >
          BACK
        </Button>
        <Button
          disabled={isLoading}
          onClick={handleSubmit}
          variant="contained"
          sx={{
            px: 4,
            bgcolor: "#383352",
            color: "white",
          }}
        >
           { isLoading ? <><CircularProgress size={20} />  LOADING </> : 'CONTINUE'}
        </Button>
      </Box>
    </div>
  );
}

export default AppointmentInsurance;
